import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { authStorageService } from '../services/authStorageService';
import { userService } from '../services/userService';
import { storageService } from '../services/storageService';
import { userActivityService } from '../services/userActivityService';
import { useDispatch } from 'react-redux';
import { resetForms } from '../reducers/formsSlice';
import { resetSubmissions } from '../reducers/submissionsSlice';
import { resetDashboard } from '../reducers/dashboardSlice';
import { resetSync } from '../reducers/syncSlice';
import { cacheService } from '../services/cacheService';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, rtdb } from '../lib/firebase';
import { ref, set } from 'firebase/database';
import { setupPresence, setupTabCloseHandlers } from '../services/presenceService';

export const AuthContext = createContext({
  user: null,
  location: null,
  loading: true,
  isLoginProcessing: false,
  organization: null,
  isAuthenticated: false,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  fetchUserDetails: () => Promise.resolve(),
  updateUser: () => {},
  firebaseUser: null
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoginProcessing, setIsLoginProcessing] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();


  const fetchUserDetails = useCallback((showInactiveModal = false) => {
    return userService
      .getAccountDetails()
      .then(response => {
        const freshUserData = response.data.data || response.data;

        if (freshUserData.is_active === false) {
          if (showInactiveModal) {
             userActivityService.checkUserActivity(true);
          }
          return;
        }

        setUser(currentUser => {
          const mergedUser = { ...currentUser, ...freshUserData };
          authStorageService.getAuthData().then(authData => {
            if (authData) {
              authStorageService.storeAuthData({ ...authData, user: mergedUser });
            }
          });
          return mergedUser;
        });

        const orgName = freshUserData.organization_name || freshUserData.organization?.name;
        const orgLogo = freshUserData.organization_logo || freshUserData.organization?.logo;
        if (orgName) {
          const orgDetails = { name: orgName, logo: orgLogo };
          setOrganization(orgDetails);
          authStorageService.storeOrganizationDetails(orgDetails);
        }
      })
      .catch(() => {});
  }, []);

  // Subscribe to Firebase Auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (fUser) => {
      setFirebaseUser(fUser);
      if (fUser) {
        console.log('✅ Firebase Session Active:', fUser.uid);
        // Set user as online in RTDB — auto-resets to offline on disconnect
        setupPresence(fUser.uid);
        // Also set offline instantly on tab close/hide (supplements onDisconnect)
        setupTabCloseHandlers();
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      authStorageService.getAuthData(),
      authStorageService.getOrganizationDetails()
    ])
      .then(([authData, orgDetails]) => {
        if (authData?.user) {
          if (authData.user.is_active === false) {
             userActivityService.checkUserActivity(true);
             setLoading(false);
             return;
          }
          setUser(authData.user);
          if (orgDetails) {
            setOrganization(orgDetails);
          }
          fetchUserDetails(true);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fetchUserDetails]);

  const login = useCallback(
    (credentials, rememberMe) => {
      setIsLoginProcessing(true);
      return storageService.clearAllData().then(() => {
        return authService
          .login(credentials)
          .then(response => {
            const responseData = response?.data?.data;
            const userData = responseData?.user;
            const token = responseData?.access_token;

            if (userData && token) {
               if (userData.is_active === false) {
                userActivityService.checkUserActivity(true);
                return authStorageService.storeAuthData({ user: userData, accessToken: token })
                  .then(() => Promise.reject(new Error('Your account is inactive. Please contact your administrator.')));
              }

              setUser(userData);

              const orgName = userData.organization_name || userData.organization?.name;
              const orgLogo = userData.organization_logo || userData.organization?.logo;
              if (orgName) {
                const orgDetails = { name: orgName, logo: orgLogo };
                setOrganization(orgDetails);
                authStorageService.storeOrganizationDetails(orgDetails);
              }

              return authStorageService
                .storeAuthData({ user: userData, accessToken: token })
                .then(() => {
                  if (rememberMe) {
                    return authStorageService.storeRememberMeData(credentials.email, credentials.password);
                  }
                  return authStorageService.clearRememberMeData();
                })
                .then(() => {
                  navigate(userData.is_first_login ? '/set-password' : '/dashboard', { replace: true });
                  return response;
                });
            }
            return Promise.reject(new Error('Invalid login response'));
          })
          .catch(err => {
            setIsLoginProcessing(false);
            return Promise.reject(err);
          })
          .finally(() => {
            setIsLoginProcessing(false);
          });
      });
    },
    [navigate]
  );

  const logout = useCallback(async (reason = null, email = null) => {
    setIsLoginProcessing(true);

    // 1. Set presence to offline immediately (don't wait for TCP disconnect)
    try {
      const firebaseUid = auth.currentUser?.uid;
      if (firebaseUid) {
        await set(ref(rtdb, `status/${firebaseUid}`), {
          state: 'offline',
          last_changed: Date.now(),
        });
      }
    } catch { /* non-fatal */ }

    // 2. Sign out of Firebase
    try { await signOut(auth); } catch {}

    // 3. Call Laravel logout API
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout API call failed, proceeding with client-side cleanup.', error);
    }

    // 4. Clear local state and redirect
    try {
        setUser(null);
        setOrganization(null);

        dispatch(resetForms());
        dispatch(resetSubmissions());
        dispatch(resetDashboard());
        dispatch(resetSync());
        
        cacheService.clear();

        await storageService.clearAllBrowserStorage();

        let redirectUrl = '/login';
        const params = new URLSearchParams();
        if (reason) params.append('reason', reason);
        if (email) params.append('email', email);
        
        const queryString = params.toString();
        if (queryString) redirectUrl += `?${queryString}`;

        navigate(redirectUrl, { replace: true });
        window.location.reload();
    } finally {
        setIsLoginProcessing(false);
    }
}, [navigate, dispatch]);




  const updateUser = useCallback((newUserData, localImageUrl = null) => {
    setUser(currentUser => {
      const updatedUser = { ...currentUser, ...newUserData };
      if (localImageUrl) {
        updatedUser.profile_image_local = localImageUrl;
      }

      authStorageService.getAuthData().then(authData => {
        if (authData) {
          authStorageService.storeAuthData({ ...authData, user: updatedUser });
        }
      });

      return updatedUser;
    });
  }, []);

  const value = useMemo(
    () => ({
      user,
      location,
      loading,
      isLoginProcessing,
      organization,
      isAuthenticated: !!user,
      login,
      logout,
      fetchUserDetails,
      updateUser,
      firebaseUser
    }),
    [user, location, loading, isLoginProcessing, organization, login, logout, fetchUserDetails, updateUser, firebaseUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};